import { useState, useRef } from 'react'
import { motion } from 'framer-motion'
import { Heart, Clock, Users, TrendingUp, Zap } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { Card, CardContent } from '../ui/Card'
import { Button } from '../ui/Button'
import { Badge } from '../ui/Badge'
import { Progress } from '../ui/Progress'
import { cn } from '../../lib/utils'
import { getCurrencySymbol } from '../../constants/index.js'

const ProjectCard = ({
  project,
  className,
  onSupport,
  onFavorite,
  isFavorited = false,
  variant = 'grid', 
  isOwner = false, 
  ...props
}) => {
  const navigate = useNavigate()
  const [isHovered, setIsHovered] = useState(false)
  const videoRef = useRef(null)
  const progressPercentage = project.funding_progress || 0
  
  const handleMouseEnter = () => {
    setIsHovered(true)
    if (videoRef.current) {
      videoRef.current.play().catch(e => console.log('Video autoplay prevented:', e))
    }
  }

  const handleMouseLeave = () => {
    setIsHovered(false)
    if (videoRef.current) {
      videoRef.current.pause()
      videoRef.current.currentTime = 0
    }
  }

  const handleCardClick = () => {
    navigate(`/project/${project.slug}`)
  }

  const handleQuickFund = (e) => {
    e.stopPropagation()
    onSupport?.(project.id)
  }

  if (variant === 'list') {
    return (
      <motion.div
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.3 }}
        className={cn("group", className)}
        {...props}
      >
        <Card
          className={cn(
            "overflow-hidden border-0 shadow-soft hover:shadow-medium transition-all duration-300 cursor-pointer",
            isOwner && "ring-2 ring-secondary-orange/20 bg-gradient-to-br from-secondary-orange/5 to-transparent"
          )}
          onClick={handleCardClick}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <CardContent className="p-4">
            <div className="flex gap-4">
              {/* Thumbnail */}
              <div className="relative w-24 h-24 flex-shrink-0 rounded-lg overflow-hidden bg-gradient-to-br from-neutral-100 to-neutral-200">
                {project.video_url ? (
                  <video 
                    ref={variant === 'list' ? videoRef : null}
                    src={project.video_url} 
                    poster={project.cover_image}
                    muted
                    loop
                    playsInline
                    className="w-full h-full object-cover"
                  />
                ) : project.cover_image ? (
                  <img 
                    src={project.cover_image} 
                    alt={project.title}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-neutral-400">
                    <TrendingUp className="w-6 h-6" />
                  </div>
                )}
                {project.is_app_listing && (
                  <Badge
                    variant="default"
                    className="absolute top-1 left-1 text-xs px-1 py-0"
                  >
                    App
                  </Badge>
                )}
                {isOwner && (
                  <Badge
                    variant="default"
                    className="absolute top-1 right-1 text-xs px-2 py-1 bg-secondary-orange/90 backdrop-blur-sm text-white"
                  >
                    My Project
                  </Badge>
                )}
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1 min-w-0">
                    <h3 className="text-p1-m font-heading text-neutral-900 line-clamp-1 group-hover:text-primary-teal transition-colors">
                      {project.title}
                    </h3>
                    <Badge variant="secondary" className="text-xs mt-1">
                      {project.category}
                    </Badge>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className={cn(
                      "h-8 w-8 flex-shrink-0 ml-2",
                      isFavorited && "text-secondary-red"
                    )}
                    onClick={(e) => {
                      e.stopPropagation()
                      onFavorite?.(project.id)
                    }}
                  >
                    <Heart className={cn("h-3 w-3", isFavorited && "fill-current")} />
                  </Button>
                </div>

                <p className="text-p2-r text-neutral-600 line-clamp-2 mb-3">
                  {project.story ? project.story.substring(0, 120) + '...' : 'No description available'}
                </p>

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4 text-p2-r text-neutral-600">
                    <span className="text-p2-m text-neutral-900">
                      {getCurrencySymbol(project.currency)}{new Intl.NumberFormat('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(project.current_funding || 0)}
                    </span>
                    <span className="text-primary-teal font-medium">
                      {new Intl.NumberFormat('en-US', { maximumFractionDigits: 1 }).format(progressPercentage)}%
                    </span>
                    <div className="flex items-center gap-1">
                      <Users className="h-3 w-3" />
                      <span>{project.donor_count || 0}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      <span>
                        {project.status === 'completed' ? 'Campaign Ended' : `${project.days_remaining || 0}d`}
                      </span>
                    </div>
                  </div>
                  
                  {isHovered && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.8 }}
                    >
                      <Button 
                        size="sm" 
                        className="h-8 px-3"
                        onClick={handleQuickFund}
                      >
                        <Zap className="h-3 w-3 mr-1" />
                        Fund
                      </Button>
                    </motion.div>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    )
  }

  // Grid variant (default)
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      whileHover={{ y: -2 }}
      className={cn("group", className)}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      {...props}
    >
      <Card
        className={cn(
          "overflow-hidden border-0 shadow-soft hover:shadow-medium transition-all duration-300 cursor-pointer h-full aspect-square rounded-lg",
          isOwner && "ring-2 ring-secondary-orange/20 bg-gradient-to-br from-secondary-orange/5 to-transparent"
        )}
        onClick={handleCardClick}
      >
        {/* Project Image/Video */}
        <div className="relative h-1/2 bg-gradient-to-br from-neutral-100 to-neutral-200 overflow-hidden">
          {project.video_url ? (
            <video 
              ref={variant !== 'list' ? videoRef : null}
              src={project.video_url} 
              poster={project.cover_image}
              muted
              loop
              playsInline
              className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
            />
          ) : project.cover_image ? (
            <img
              src={project.cover_image}
              alt={project.title}
              className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-neutral-400">
              <TrendingUp className="w-8 h-8" />
            </div>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/10 via-transparent to-transparent" />
          
          {/* Badges and Actions */}
          <div className="absolute top-2 left-2 right-2 flex justify-between items-start">
            <div className="flex gap-1">
              {project.is_app_listing && (
                <Badge
                  variant="default"
                  className="text-xs px-2 py-1 bg-primary-teal/90 backdrop-blur-sm"
                >
                  App
                </Badge>
              )}
              <Badge
                variant="secondary"
                className="text-xs px-2 py-1 bg-white/90 backdrop-blur-sm text-neutral-700"
              >
                {project.category}
              </Badge>
              {isOwner && (
                <Badge
                  variant="default"
                  className="text-xs px-2 py-1 bg-secondary-orange/90 backdrop-blur-sm text-white"
                >
                  My Project
                </Badge>
              )}
            </div>
            
            <Button
              variant="ghost"
              size="icon"
              className={cn(
                "h-8 w-8 bg-white/90 backdrop-blur-sm hover:bg-white transition-all duration-200",
                isFavorited && "text-secondary-red"
              )}
              onClick={(e) => {
                e.stopPropagation()
                onFavorite?.(project.id)
              }}
            >
              <Heart className={cn("h-3 w-3", isFavorited && "fill-current")} />
            </Button>
          </div>

          {/* Quick Fund Button - appears on hover */}
          {isHovered && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 10 }}
              className="absolute bottom-2 right-2"
            >
              <Button 
                size="sm" 
                className="h-8 px-3 bg-primary-teal/90 backdrop-blur-sm hover:bg-primary-teal"
                onClick={handleQuickFund}
              >
                <Zap className="h-3 w-3 mr-1" />
                Fund
              </Button>
            </motion.div>
          )}
        </div>

        <CardContent className="p-4">
          {/* Project Title */}
          <h3 className="text-p1-m font-heading text-neutral-900 mb-2 line-clamp-1 group-hover:text-primary-teal transition-colors">
            {project.title}
          </h3>
          
          {/* Project Description */}
          <p className="text-p2-r text-neutral-600 mb-3 line-clamp-2">
            {project.story ? project.story.substring(0, 100) + '...' : 'No description available'}
          </p>

          {/* Funding Progress */}
          <div className="space-y-2">
            <Progress value={progressPercentage} className="h-1" />
            
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-p2-m text-neutral-900">
                  {getCurrencySymbol(project.currency)}{new Intl.NumberFormat('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(project.current_funding || 0)}
                </span>
                <span className="text-xs text-primary-teal font-medium bg-primary-teal/10 px-2 py-1 rounded-full">
                  {new Intl.NumberFormat('en-US', { maximumFractionDigits: 1 }).format(progressPercentage)}%
                </span>
              </div>
              <span className="text-p3-r text-neutral-500">
                {getCurrencySymbol(project.currency)}{Math.round(project.funding_goal || 0).toLocaleString()} goal
              </span>
            </div>
            
            <div className="flex items-center justify-between text-p3-r text-neutral-600">
              <div className="flex items-center gap-1">
                <Users className="h-3 w-3" />
                <span>{project.donor_count || 0} donors</span>
              </div>
              <div className="flex items-center gap-1">
                <Clock className="h-3 w-3" />
                <span>
                  {project.status === 'completed' ? 'Campaign Ended' : `${project.days_remaining || 0} days left`}
                </span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  )
}

export default ProjectCard
